import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (endpoint, params = '') => `${endpoint}${params}`;

const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
};

const fetchWithCache = async (endpoint, forceRefresh = false) => {
  const cacheKey = getCacheKey(endpoint);
  const cachedData = cache.get(cacheKey);

  if (!forceRefresh && isCacheValid(cachedData)) {
    return cachedData.data;
  }

  const response = await axios.get(`${API_URL}${endpoint}`);
  cache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });

  return response.data;
};

export const fetchOrganisations = () => fetchWithCache('/api/solicitors');
export const fetchOrganisation = (id) => fetchWithCache(`/api/solicitors/${id}`);
export const fetchReviews = async (id, page = 1, forceRefresh = false) => {
  // Backwards compatibility: if caller used old signature (id, forceRefresh)
  if (typeof page === 'boolean') {
    forceRefresh = page;
    page = 1;
  }
  try {
    console.log(`Fetching reviews for organisation ${id} page ${page}, forceRefresh: ${forceRefresh}`);
    const cacheKey = getCacheKey(`/api/organisations/${id}/reviews?page=${page}`);
    
    if (!forceRefresh) {
      const cachedData = cache.get(cacheKey);
      if (isCacheValid(cachedData)) {
        console.log(`Using cached reviews for ${id}`, cachedData.data);
        return cachedData.data;
      }
    }
    
    console.log(`Making API request for reviews for organisation ${id} (page ${page})`);
    const endpoint = `/api/organisations/${id}/reviews?page=${page}`;
    const result = await fetchWithCache(endpoint, forceRefresh);
    console.log(`Received reviews for ${id}:`, result);
    return result;
  } catch (error) {
    console.error(`Error fetching reviews for ID ${id}:`, error);
    return { reviews: [], stats: null };
  }
};
export const fetchFilters = () => fetchWithCache('/api/filters');
export const fetchQuestions = (id) => fetchWithCache(`/api/organisations/${id}/questions`);

export const syncSRAData = async () => {
  try {
    // Clear cache when syncing
    cache.clear();
    const response = await axios.post(`${API_URL}/api/sync-sra-data`);
    return response.data;
  } catch (error) {
    console.error('Error syncing SRA data:', error);
    throw error;
  }
};

export const postQuestion = async (id, questionData) => {
  const response = await axios.post(`${API_URL}/api/organisations/${id}/questions`, questionData);
  return response.data;
};

export const submitReview = async (id, reviewData) => {
  try {
    const response = await axios.post(`${API_URL}/api/organisations/${id}/reviews`, reviewData);
    // Clear the reviews cache for this organisation
    const cacheKey = getCacheKey(`/api/organisations/${id}/reviews`);
    cache.delete(cacheKey);
    return response.data;
  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
};

export const updateReview = async (reviewId, reviewData) => {
  try {
    const response = await axios.put(`${API_URL}/api/reviews/${reviewId}`, reviewData);
    // We need to invalidate the cache for the organization that this review belongs to
    // But we don't know which organization without fetching the review first
    // So we'll just clear all review-related cache entries
    for (const key of cache.keys()) {
      if (key.includes('/reviews')) {
        cache.delete(key);
      }
    }
    return response.data;
  } catch (error) {
    console.error('Error updating review:', error);
    throw error;
  }
};

export const deleteReview = async (reviewId) => {
  try {
    const response = await axios.delete(`${API_URL}/api/reviews/${reviewId}`);
    // Clear all review caches
    for (const key of cache.keys()) {
      if (key.includes('/reviews')) {
        cache.delete(key);
      }
    }
    return response.data;
  } catch (error) {
    console.error('Error deleting review:', error);
    throw error;
  }
}; 